import { defineStore } from 'pinia'
import { doc, onSnapshot, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { LabCustomerUserBusiness } from '@/globals/typescript/models/LabCustomerUserBusiness'
import { LabCustomerUserInsurance } from '@/globals/typescript/models/LabCustomerUserInsurance'
import { LabCustomerUserPrivate } from '@/globals/typescript/models/LabCustomerUserPrivate'
import { isLabCustomerCompanyUser } from '@team-milva/lab-models/user'
import type { DocumentData } from 'firebase/firestore'
import { CURRENT_TERMS_OF_USE_VERSION } from '@/globals/typescript/utils/termsOfUse'
import { getOwnInsuranceCustomerAccountID } from '@/globals/typescript/utils/insuranceOwner'
import { customerApi } from '@/globals/typescript/utils/api/customerClient'
import { i18n, SUPPORTED_LOCALES, type TAppLocale } from '@/locales'
import {
  LAB_CUSTOMER_LANGUAGES,
  type TLabCustomerLanguageCode,
} from '@team-milva/lab-models/language'
import type { LabAccount } from '@team-milva/lab-models/labAccount'
import type { TCustomerOwnerAccount } from '@/stores/accountStore'
import {
  customerOwnerApi,
  customerOwnerContext,
} from '@/globals/typescript/utils/api/customerOwnerApi'
import type { PhoneNumberMeta } from '@team-milva/utils/phone'
import * as Sentry from '@sentry/vue'

export type TCurrentUser =
  | LabCustomerUserBusiness
  | LabCustomerUserInsurance
  | LabCustomerUserPrivate

// The model class for the signed-in user's document, by its `type` (MIL-402). Anything that is
// neither private nor insurance is business, as before.
export function toCurrentUser(data: DocumentData): TCurrentUser {
  if (data.type === 'lab-customer-user-private') {
    return new LabCustomerUserPrivate(data as LabCustomerUserPrivate)
  }
  if (data.type === 'lab-customer-user-insurance') {
    return new LabCustomerUserInsurance(data as LabCustomerUserInsurance)
  }
  return new LabCustomerUserBusiness(data as LabCustomerUserBusiness)
}

// Only apply a resolved language to the running i18n instance when this build
// can actually render it — same safety net as LangSelect.vue's narrowing and
// brandStore's isSupportedLocale. See "Per-Lab & Per-Customer Language
// Selection", Phase 1's Decisions Made for why this stays even after Phase 4.
function applyLocaleIfSupported(locale: string | undefined): void {
  if (!locale || !(SUPPORTED_LOCALES as readonly string[]).includes(locale)) return
  i18n.global.locale.value = locale as TAppLocale
  localStorage.setItem('lang', locale)
}

// Resolves what a genuine first-login user's language should default to:
// the CustomerAccount's own configured default when the account has actually
// configured its own supportedLocales (a non-empty list is real signal — an
// empty one just means the class default kicked in, not a deliberate choice),
// else the lab-wide defaultCustomerLanguageCode, else CA's hardcoded 'EN'.
// Mirrors the account-set → lab-set → default chain used elsewhere in this
// plan (see accountStore.availableLanguages, Phase 2).
function resolveDefaultLanguageCode(
  labAccount: LabAccount | null,
  customerAccount: TCustomerOwnerAccount | null
): TLabCustomerLanguageCode {
  if (customerAccount && customerAccount.supportedLocales.length > 0) {
    const matchingCode = (
      Object.entries(LAB_CUSTOMER_LANGUAGES) as [
        TLabCustomerLanguageCode,
        (typeof LAB_CUSTOMER_LANGUAGES)[TLabCustomerLanguageCode],
      ][]
    ).find(([, language]) => language.locale === customerAccount.defaultLocale)?.[0]

    if (matchingCode) return matchingCode
  }

  return labAccount?.defaultCustomerLanguageCode ?? 'EN'
}

// The language codes this user's account lets them be on: the lab's customer languages, narrowed to
// the CustomerAccount's own supportedLocales when it has configured any (same rule as
// accountStore.availableLanguages). Null when the lab account isn't loaded, i.e. nothing to judge by.
function resolveAllowedLanguageCodes(
  labAccount: LabAccount | null,
  customerAccount: TCustomerOwnerAccount | null
): TLabCustomerLanguageCode[] | null {
  if (!labAccount) return null

  const supportedLocales = customerAccount?.supportedLocales ?? []
  if (supportedLocales.length === 0) return labAccount.customerLanguages

  return labAccount.customerLanguages.filter((code) =>
    (supportedLocales as readonly string[]).includes(LAB_CUSTOMER_LANGUAGES[code]?.locale)
  )
}

export const useUserStore = defineStore('user', {
  state: () => ({
    currentUser: null as TCurrentUser | null,

    unsubscribeCurrentUser: null as (() => void) | null,
  }),

  actions: {
    getCurrentUser(userID: string): Promise<TCurrentUser | null> {
      if (this.currentUserLoaded) {
        return Promise.resolve(this.currentUser)
      }

      return new Promise((resolve) => {
        let resolved = false

        const unsubscribe = onSnapshot(
          doc(db, 'Apps', '-lab-customer-', 'Users', userID),
          // Metadata changes too: a missing document first reported from the cache is only confirmed by
          // the server in a snapshot whose data is unchanged and whose `fromCache` flips — which the
          // listener skips unless it is asked for metadata-only changes.
          { includeMetadataChanges: true },
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data()
              this.currentUser = toCurrentUser(data)
              this.unsubscribeCurrentUser = unsubscribe

              // Apply the user's own persisted language on every load — the only
              // way a saved preference survives a fresh device/session, since
              // main.ts's boot sequence only ever consults localStorage + browser
              // detection before the user is known. Unconditional on every
              // snapshot, not just the first, matching lab-management's
              // userStore.ts. See "Per-Lab & Per-Customer Language Selection",
              // Phase 3. A first-login user has no languageCode yet — see
              // applyAccountDefaultLanguageIfFirstLogin below.
              applyLocaleIfSupported(
                this.currentUser.languageCode
                  ? LAB_CUSTOMER_LANGUAGES[this.currentUser.languageCode]?.locale
                  : undefined
              )

              if (!resolved) {
                resolved = true
                resolve(this.currentUser)
              }
              return
            }

            // The account is signed in but has no user document — deleted, or never in this
            // environment (e.g. an emulator restarted from a snapshot). Resolve with no user so
            // sign-in start-up can send them to login; without this the promise never settles and
            // the app never mounts. Only trusted from the server: a cache miss while offline is
            // not proof the document is gone.
            if (!resolved && !docSnap.metadata.fromCache) {
              resolved = true
              unsubscribe()
              resolve(null)
            }
          },
          (error) => {
            if (resolved) return
            resolved = true
            unsubscribe()
            Sentry.captureException(error)
            resolve(null)
          }
        )
      })
    },

    resetStore(): void {
      this.currentUser = null

      if (this.unsubscribeCurrentUser) {
        this.unsubscribeCurrentUser()
        this.unsubscribeCurrentUser = null
      }
    },

    // Firestore rules only allow a signed-in user to change their own
    // `timestamps.lastSeenAt` — firstName/lastName/phone/languageCode must go
    // through the lab-cloud update-user endpoint instead of a direct Firestore
    // write. Reused as-is for `languageCode` (self-service, not team
    // management) — see "Per-Lab & Per-Customer Language Selection", Phase 3.
    async updateOwnProfile(updates: {
      firstName?: string
      lastName?: string
      phone?: PhoneNumberMeta
      languageCode?: TLabCustomerLanguageCode
    }): Promise<void> {
      if (!this.currentUser) return

      if (this.currentUser.type === 'lab-customer-user-private') {
        // Self-service endpoint (updateOwnPrivateUser) — added alongside
        // this plan's languageCode work, fixing a pre-existing gap where
        // this path didn't exist in lab-cloud at all (every private-user
        // profile edit was a silent no-op). See "Per-Lab & Per-Customer
        // Language Selection", Decisions Made.
        const result = await customerApi(
          'patch',
          'lab-accounts/{labAccountID}/private-users/{userID}',
          {
            path: {
              labAccountID: this.currentUser.labAccountID,
              userID: this.currentUser.id,
            },
            body: updates,
          }
        )
        if (!result.success) throw new Error(result.code)
        return
      }

      // A business or an insurance user (MIL-402): the account's own update-user endpoint, on the
      // business or the insurance route tree.
      const owner = customerOwnerContext(this.currentUser)
      if (!owner) throw new Error('missing-customer')

      const result = await customerOwnerApi(owner, 'patch', '/users/{userID}', {
        path: { userID: this.currentUser.id },
        body: updates,
      })

      if (!result.success) throw new Error(result.code)
    },

    // Manual change via LangSelect.vue, once logged in — persists the choice
    // server-side (not just localStorage, which alone doesn't survive a fresh
    // device) so it can be re-applied on every future login. See "Per-Lab &
    // Per-Customer Language Selection", Phase 3.
    async updateLanguage(languageCode: TLabCustomerLanguageCode): Promise<void> {
      await this.updateOwnProfile({ languageCode })
    },

    // Brings a business or insurance user's language in line with what their account allows, and persists it so
    // future logins re-apply it like any other saved choice. Two cases, both resolved to the account
    // default (see resolveDefaultLanguageCode):
    //   - a genuine first login (no saved languageCode yet — only possible for business and
    //     insurance users;
    //     private users always get one written at account creation, see
    //     updateCustomerUserPrivateService);
    //   - a saved language the account no longer allows (the lab narrowed supportedLocales after
    //     the user picked it). Without this the user stays on it, and LangSelect is hidden once a
    //     single language remains, so they could not switch away.
    // No-op for a returning user whose language is still allowed, and for a private user — see
    // "Per-Lab & Per-Customer Language Selection", Phase 3.
    async reconcileLanguageWithAccount(
      labAccount: LabAccount | null,
      customerAccount: TCustomerOwnerAccount | null
    ): Promise<void> {
      // Business and insurance users alike (MIL-402) — both belong to an account with its own locales.
      if (!isLabCustomerCompanyUser(this.currentUser)) {
        return
      }

      const savedLanguageCode = this.currentUser.languageCode
      if (savedLanguageCode) {
        const allowedCodes = resolveAllowedLanguageCodes(labAccount, customerAccount)
        if (!allowedCodes || allowedCodes.includes(savedLanguageCode)) return
      }

      // The configured default first — but a stale default (the lab narrowed its languages after it was
      // set, or the account's `defaultLocale` is no longer in `supportedLocales`) must not put the user
      // on another disallowed language, so fall back to the first one that is allowed.
      const defaultCode = resolveDefaultLanguageCode(labAccount, customerAccount)
      const allowedCodes = resolveAllowedLanguageCodes(labAccount, customerAccount)
      const languageCode =
        !allowedCodes || allowedCodes.includes(defaultCode) ? defaultCode : (allowedCodes[0] ?? defaultCode)
      applyLocaleIfSupported(LAB_CUSTOMER_LANGUAGES[languageCode]?.locale)

      try {
        await this.updateOwnProfile({ languageCode })
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    // Records the acceptance through lab-cloud — `termsAccepted` is not a field a customer may write
    // to Firestore directly (the rules only allow `timestamps.lastSeenAt`). The server sets the
    // time. The user snapshot also delivers it, but only after this call returns, so it is set
    // locally here too, or the router guard would send the user straight back to the terms.
    async acceptTermsOfUse(version: string): Promise<void> {
      if (!this.currentUser) return

      const owner = customerOwnerContext(this.currentUser)
      if (!owner) throw new Error('missing-customer')

      const result = await customerOwnerApi(owner, 'post', '/users/{userID}/accept-terms', {
        path: { userID: this.currentUser.id },
        body: { version },
      })

      if (!result.success) throw new Error(result.code)

      this.currentUser.termsAccepted = { version, acceptedAt: Date.now() }
    },

    updateLastSeen(): void {
      if (!this.currentUser) return

      const now = Date.now()
      const interval = 5 * 60 * 1000 // 5 minutes
      const lastSeen = this.currentUser.timestamps.lastSeenAt

      if (now - lastSeen < interval) return

      const userRef = doc(
        db,
        'Apps',
        '-lab-customer-',
        'Users',
        this.currentUser.id
      )
      updateDoc(userRef, {
        'timestamps.lastSeenAt': now,
      }).catch((error) => {
        Sentry.captureException(error)
      })
    },
  },

  getters: {
    currentUserLoaded: (state) => !!state.unsubscribeCurrentUser,
    // Roles (MIL-137) only exist on business and insurance users — private users have no
    // mainRole. Per MIL-137, this app has only two roles ("admin" | "user") —
    // there is no "owner" role here (that's a lab-management-only concept,
    // see TLabManagementUser's 3-role model). "admin" is the highest-privilege
    // role in this app's model, e.g. for team-management gating.
    // Business and insurance users share the admin/user roles (MIL-402).
    isAdmin: (state) =>
      isLabCustomerCompanyUser(state.currentUser) &&
      state.currentUser.mainRole === 'admin',
    // The signed-in user's own insurance customer ID, or null for a business or private user
    // (MIL-402, Phase 6). Set means "this user orders as an insurance customer": every order and
    // project is an insurance case with this ID as the insurer, so the forms show no
    // "Forsikringssag" toggle and no insurer picker — see insuranceOwner.ts.
    ownInsuranceCustomerAccountID: (state): string | null =>
      getOwnInsuranceCustomerAccountID(state.currentUser),
    // The only thing standing between a signed-in user and the app is accepting the current Terms
    // of Service. Every customer user is created with a name and a phone number, so the old name
    // and phone onboarding steps are gone.
    needsTermsAcceptance: (state) =>
      !!state.currentUser && state.currentUser.termsAccepted?.version !== CURRENT_TERMS_OF_USE_VERSION,
  },
})
